import { Check, Mic, MicOff, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatQty, parseVoice, voiceQty, type Product } from '@sense/shared';
import { useProductIndex, search, useProducts } from '../lib/catalog';
import { isNativeApp, nativeMessage, SdDevices } from '../lib/native';
import { Button, Segmented, Sheet } from './ui';
import { tr } from '../lib/i18n';

type Lang = 'en-IN' | 'ta-IN' | 'hi-IN';
interface Row { said: string; qty: number; choices: Product[]; pick: Product | null }

interface SpeechRec { lang: string; interimResults: boolean; continuous: boolean; start(): void; stop(): void; onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null; onend: (() => void) | null; onerror: ((e: { error: string }) => void) | null }
const Recognition = (window as unknown as { SpeechRecognition?: new () => SpeechRec; webkitSpeechRecognition?: new () => SpeechRec }).SpeechRecognition
  ?? (window as unknown as { webkitSpeechRecognition?: new () => SpeechRec }).webkitSpeechRecognition;
/** Browsers with speech recognition, or the Android app (native recogniser). */
export const voiceSupported = () => !!Recognition || isNativeApp();

const ALIASES = 'sd.voiceAlias';
const aliases = (): Record<string, number> => { try { return JSON.parse(localStorage.getItem(ALIASES) ?? '{}'); } catch { return {}; } };
/** When the cashier picks a product for a spoken word, remember it so the shop's own words win next time. */
const learn = (said: string, pid: number) => localStorage.setItem(ALIASES, JSON.stringify({ ...aliases(), [said]: pid }));

/** Push-to-talk billing in English, Tamil or Hindi: say items, check the matches, add them in one go. */
export function VoiceBilling({ open, onClose, onAdd }: { open: boolean; onClose(): void; onAdd(p: Product, qty: number): void }) {
  const products = useProducts();
  const index = useProductIndex();
  const byId = useMemo(() => new Map((products ?? []).map((p) => [p.id, p])), [products]);
  const [lang, setLang] = useState<Lang>(() => (localStorage.getItem('sd.voiceLang') as Lang | null) ?? 'en-IN');
  const [listening, setListening] = useState(false);
  const [text, setText] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);
  const rec = useRef<SpeechRec | null>(null);

  const understand = (said: string) => {
    const known = aliases();
    setRows(parseVoice(said).map((it) => {
      const alias = known[it.words] != null ? byId.get(known[it.words]) : undefined;
      const choices = alias ? [alias] : search(index, it.words, 3).matches.map((m) => m.product);
      const pick = alias ?? (choices.length === 1 ? choices[0] : null);
      const base = pick ?? choices[0];
      const qty = base ? voiceQty(it, base.unit) ?? (base.unit === 'g' || base.unit === 'ml' ? 100_000 : 1000) : 1000;
      return { said: it.words, qty, choices, pick };
    }));
  };

  const start = () => {
    setError(null); setText(''); setRows([]);
    if (!Recognition && isNativeApp()) {
      setListening(true);
      let sub: { remove(): Promise<void> } | null = null;
      void SdDevices.addListener('speech', (d) => { if (d.text) setText(d.text); }).then((h) => { sub = h; });
      void SdDevices.listen({ lang })
        .then(({ text: said }) => { setText(said); understand(said); })
        .catch((e) => setError(nativeMessage(e)))
        .finally(() => { setListening(false); void sub?.remove(); });
      return;
    }
    if (!Recognition) return;
    const r = new Recognition();
    r.lang = lang; r.interimResults = true; r.continuous = false;
    r.onresult = (e) => {
      const said = Array.from(e.results).map((x) => x[0].transcript).join(' ');
      setText(said);
      if (Array.from(e.results).every((x) => x.isFinal)) understand(said);
    };
    r.onerror = (e) => setError(e.error === 'not-allowed' ? 'Allow the microphone for this site to bill by voice.' : 'Didn’t catch that. Try again.');
    r.onend = () => setListening(false);
    rec.current = r;
    r.start();
    setListening(true);
  };
  useEffect(() => { if (!open) { rec.current?.stop(); setListening(false); } }, [open]);

  const ready = rows.filter((r) => r.pick);
  const addAll = () => {
    for (const r of ready) onAdd(r.pick!, r.qty);
    onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} title={tr('Bill by voice')}
      footer={<div className="flex items-center justify-between gap-2 pb-1"><span className="text-sm text-muted">{tr('{0} of {1} ready', { 0: ready.length, 1: rows.length })}</span><Button disabled={!ready.length} onClick={addAll}><Check size={18} />{tr('Add {0} items', { 0: ready.length })}</Button></div>}>
      <div className="flex flex-col gap-4 pb-2">
        <Segmented label={tr('Language')} value={lang} onChange={(l) => { setLang(l); localStorage.setItem('sd.voiceLang', l); }} options={[['en-IN', tr('English')], ['ta-IN', 'தமிழ்'], ['hi-IN', 'हिन्दी']]} />
        <button onClick={() => (listening ? (rec.current ? rec.current.stop() : void SdDevices.stopListening().catch(() => {})) : start())}
          className={`mx-auto flex h-24 w-24 items-center justify-center rounded-full text-white shadow-lg ${listening ? 'animate-pulse bg-danger' : 'bg-forest-700'}`} aria-label={listening ? 'Stop listening' : 'Start listening'}>
          {listening ? <MicOff size={36} /> : <Mic size={36} />}
        </button>
        <p className="text-center text-sm text-muted">{listening ? tr('Listening… say items like “two kilo onion, half kilo tomato, 3 milk”') : lang === 'ta-IN' ? 'தட்டி பேசுங்கள்: “ரெண்டு கிலோ வெங்காயம், அரை கிலோ தக்காளி”' : lang === 'hi-IN' ? 'टैप करके बोलें: “दो किलो प्याज़, आधा किलो टमाटर”' : tr('Tap and say items: “two kilo onion, half kilo tomato, 3 milk”')}</p>
        {text && <div className="rounded-2xl bg-paper px-4 py-3 text-center font-bold">“{text}”</div>}
        {error && <div className="rounded-2xl bg-t-red px-4 py-3 text-sm font-bold text-danger">{error}</div>}
        {rows.map((r, i) => (
          <div key={i} className={`rounded-2xl border p-3 ${r.pick ? 'border-line bg-forest-50' : r.choices.length ? 'border-t-amber bg-t-amber' : 'border-line'}`}>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-bold">“{r.said}”</span><span className="num text-muted">{r.pick ? formatQty(r.qty, r.pick.unit) : ''}</span>
              <span className="flex-1" />
              {r.pick && <span className="truncate font-bold text-primary">{r.pick.name}</span>}
              <button aria-label={`Remove ${r.said}`} onClick={() => setRows(rows.filter((_, j) => j !== i))} className="flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-white"><X size={16} /></button>
            </div>
            {!r.pick && (r.choices.length ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="text-xs font-bold text-t-amber-fg">{tr('Which one?')}</span>
                {r.choices.map((p) => (
                  <button key={p.id} onClick={() => { learn(r.said, p.id); setRows(rows.map((x, j) => (j === i ? { ...x, pick: p, qty: x.qty } : x))); }}
                    className="rounded-full border border-line bg-white px-3 py-1 text-sm font-bold hover:border-forest-600">{p.name}</button>
                ))}
              </div>
            ) : <div className="mt-1 text-xs text-muted">{tr('Not in your catalog. Try the product’s name, or add a Tamil name to it in Inventory.')}</div>)}
          </div>
        ))}
      </div>
    </Sheet>
  );
}
